/*
 * Control-affordance mapping — derived directly from the U24 state-machine
 * diagram (17 states + the pause/resume/cancel control overlay) and the U20
 * control endpoints' 409 semantics.
 *
 * This is the single source of truth for "which control buttons are live for a
 * given run". The backend still enforces every transition (pause/resume/cancel
 * return 409 in an invalid state); this only avoids dangling controls an
 * operator can't actually use, and explains *why* each is disabled.
 *
 * The run's `status` and `control` together decide affordances:
 *   - a terminal status (completed / aborted / failed) disables everything;
 *   - a pending control (pausing… / cancelling…) means the worker is mid-drain,
 *     so we don't stack a second control request on top;
 *   - pause is only offered from a state the worker can actually drain from;
 *   - resume is only offered from the two parked states (paused / needs_reauth);
 *   - cancel is offered from any non-terminal, non-already-cancelling state.
 */

import { isTerminalRunState, type RunState } from "../../lib/status";

export type ControlAction = "pause" | "resume" | "redrive" | "cancel" | "force-cancel";

/** Normalise a backend status/control string the way `status.ts` does. */
function norm(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

/*
 * The states from which a cooperative pause is meaningful. Per the diagram only
 * the long-running drainable stages enter PAUSED: the worker checks `control`
 * at every stage boundary and between each finalize ticket/batch. The terminal
 * holds (paused / needs_reauth) and the brief bookkeeping/recovery tail are not
 * pausable — pausing them is a no-op the backend rejects (409).
 */
const PAUSABLE_STATES: ReadonlySet<RunState> = new Set<RunState>([
  "prepping",
  "prepped",
  "uploading",
  "uploaded",
  "finalizing",
  "finalized",
  "auditing",
  "recheck_pending",
  "recovering",
  "queued",
  "blocked",
]);

/**
 * The parked states a resume re-drives from (reconcile vs live Meta).
 * `recovering` (2026-07-08): a run holding on needs_human discrepancies —
 * Resume re-enters at AUDITING, which re-reads Meta fresh and rebuilds the
 * report, so an operator's external fix in Ads Manager flows through to
 * COMPLETED (the run pager's RECOVERY_HELD advice).
 */
const RESUMABLE_STATES: ReadonlySet<RunState> = new Set<RunState>([
  "paused",
  "needs_reauth",
  "recovering",
]);

export interface ControlAvailability {
  enabled: boolean;
  /** Human-readable reason when disabled (drives the button title/tooltip). */
  reason?: string;
}

export interface RunControlState {
  status: string;
  control: string;
}

/** Is a control request already in flight (worker draining)? */
export function hasPendingControl(control: string): boolean {
  const c = norm(control);
  return c === "pausing" || c === "cancelling";
}

/**
 * Resolve whether a given control action is available for a run in
 * (`status`, `control`). Returns the enabled flag plus a disabled-reason so the
 * UI can explain itself (a disabled button with a tooltip beats a hidden one).
 */
export function controlAvailability(
  action: ControlAction,
  { status, control }: RunControlState,
): ControlAvailability {
  const s = norm(status) as RunState;
  const c = norm(control);

  // Re-drive targets exactly ONE terminal state (FAILED), so it is resolved
  // BEFORE the blanket terminal guard below.
  if (action === "redrive") {
    return s === "failed"
      ? { enabled: true }
      : { enabled: false, reason: "Only a FAILED run can be re-driven." };
  }

  if (isTerminalRunState(s)) {
    return { enabled: false, reason: "Run is in a terminal state." };
  }

  // Force-cancel (super-admin escape hatch) is valid from ANY non-terminal state —
  // INCLUDING a run wedged in control='cancelling' (the exact case the cooperative
  // cancel can't recover). So it bypasses the pending-control gate below.
  if (action === "force-cancel") {
    return { enabled: true };
  }

  // A drain is already underway — don't stack another control request on it.
  if (c === "pausing") {
    return action === "cancel"
      ? { enabled: true }
      : { enabled: false, reason: "A pause is already draining." };
  }
  if (c === "cancelling") {
    return { enabled: false, reason: "A cancel is already draining." };
  }

  switch (action) {
    case "pause": {
      if (RESUMABLE_STATES.has(s)) {
        return { enabled: false, reason: "Run is already paused." };
      }
      if (!PAUSABLE_STATES.has(s)) {
        return {
          enabled: false,
          reason: "This stage can't be paused mid-flight.",
        };
      }
      return { enabled: true };
    }
    case "resume": {
      if (!RESUMABLE_STATES.has(s)) {
        return {
          enabled: false,
          reason: "Run is not parked (paused / needs reauth / recovering).",
        };
      }
      return { enabled: true };
    }
    case "cancel": {
      // Cancel is valid from any live or parked state (drains then ABORTED).
      return { enabled: true };
    }
  }
}

/** True when the run is parked awaiting a credential rotation. */
export function needsReauth(status: string): boolean {
  return norm(status) === "needs_reauth";
}

/** True when the run is parked in PAUSED. */
export function isPaused(status: string): boolean {
  return norm(status) === "paused";
}
